import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { Logo } from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ChangePassword() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.new_password !== formData.confirm_password) {
      setError('كلمة المرور الجديدة غير متطابقة');
      return;
    }

    if (formData.new_password.length < 6) {
      setError('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/auth/change-password`,
        {
          old_password: formData.old_password,
          new_password: formData.new_password,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const updatedUser = { ...user, first_login: false };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'فشل تغيير كلمة المرور');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand flex items-center justify-center py-12" dir="rtl" lang="ar">
      <div className="w-full max-w-md px-6">
        <div className="bg-white rounded-xl shadow-xl p-8">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Logo size="lg" />
            <h1 className="text-2xl font-heading font-bold text-primary">تغيير كلمة المرور</h1>
          </div>

          {user?.first_login && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800 font-body">
                <strong>مرحباً!</strong> هذا هو دخولك الأول. يُرجى تغيير كلمة المرور الافتراضية.
              </p>
            </div>
          )}

          {success ? (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-heading font-bold text-primary mb-2">
                تم تغيير كلمة المرور بنجاح!
              </h2>
              <p className="text-gray-600 font-body">جاري التحويل...</p>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-center gap-3" data-testid="error-message">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <p className="text-sm text-red-700 font-body">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                    كلمة المرور القديمة <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Lock className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      data-testid="old-password-input"
                      type={showOld ? 'text' : 'password'}
                      name="old_password"
                      value={formData.old_password}
                      onChange={handleChange}
                      required
                      className="w-full pr-10 pl-12 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                    />
                    <button
                      type="button"
                      onClick={() => setShowOld(!showOld)}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showOld ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                    كلمة المرور الجديدة <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Lock className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      data-testid="new-password-input"
                      type={showNew ? 'text' : 'password'}
                      name="new_password"
                      value={formData.new_password}
                      onChange={handleChange}
                      required
                      className="w-full pr-10 pl-12 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNew(!showNew)}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showNew ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                    تأكيد كلمة المرور <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Lock className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      data-testid="confirm-password-input"
                      type={showConfirm ? 'text' : 'password'}
                      name="confirm_password"
                      value={formData.confirm_password}
                      onChange={handleChange}
                      required
                      className="w-full pr-10 pl-12 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <button
                  data-testid="change-password-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gold text-white py-3 rounded-md hover:bg-gold-dark transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 font-heading font-medium"
                >
                  {loading ? 'جاري التغيير...' : 'تغيير كلمة المرور'}
                </button>
              </form>

              {!user?.first_login && (
                <div className="mt-6 text-center">
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="text-sm text-gray-500 hover:text-primary font-body"
                  >
                    العودة إلى لوحة التحكم
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
